/**
 * HOTDataGrid - Handsontable wrapper for ConfigurableBaseExplorer
 *
 * Replaces the native HTML table with Handsontable while preserving
 * all existing features: sorting, pagination, field transformers, actions.
 */

import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import { HotTable, HotTableProps, HotTableRef } from '@handsontable/react-wrapper';
import Handsontable from 'handsontable';
import {
  BaseEntity,
  BaseExplorerConfig,
  ColumnConfig,
  ExplorerFeatureConfig,
  FieldTransformer
} from '../../../types/baseExplorerConfig';
import {
  createTransformerRenderer,
  createActionsRenderer,
  createSortableHeaderRenderer
} from '../../../utils/hotRenderers';
import './HOTDataGrid.scss';

// Sort configuration matching ConfigurableBaseExplorer
interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export interface HOTDataGridProps<T extends BaseEntity> {
  data: T[];
  columns: ColumnConfig[];
  sortConfig: SortConfig | null;
  onSort: (field: string) => void;
  transformFieldValue: (entity: T, field: keyof T) => string;
  features: ExplorerFeatureConfig;
  config: BaseExplorerConfig<T>;
  onEntitySelect?: (entity: T) => void;
  /**
   * Optional dedicated handler for the per-row View button. When omitted, the
   * View button falls back to onEntityEdit (the long-standing single-handler
   * convention used by most explorers). Explorers that need View ≠ Edit
   * (e.g. WSE, where View opens a read-only modal and Edit opens the form)
   * can wire this to split the two.
   */
  onEntityView?: (entity: T) => void;
  onEntityEdit?: (entity: T) => void;
  onEntityDelete?: (entity: T) => void;
  licenseKey?: string;
  height?: number | string;
  onFilterClick?: (field: string) => void;
  activeFilters?: Record<string, any>;
  /** Per-user persisted widths (keyed by column field). When set for a
   *  column, overrides schema width and autoColumnSize. */
  savedColumnWidths?: Record<string, number>;
  /** Fired after the user manually drags a column edge to resize. Lets the
   *  parent persist the new width per user. */
  onColumnResize?: (field: string, width: number) => void;
}

export default function HOTDataGrid<T extends BaseEntity>({
  data,
  columns,
  sortConfig,
  onSort,
  transformFieldValue,
  features,
  config,
  onEntitySelect,
  onEntityView,
  onEntityEdit,
  onEntityDelete,
  licenseKey = 'non-commercial-and-evaluation',
  height = 'auto',
  onFilterClick,
  activeFilters = {},
  savedColumnWidths,
  onColumnResize,
}: HOTDataGridProps<T>) {
  const hotRef = useRef<HotTableRef>(null);

  // Get entity by row index
  const getEntity = useCallback((row: number): T | undefined => {
    return data[row];
  }, [data]);

  // Check if actions column should be shown
  const showActionsColumn = features.editing?.enabled || !!onEntitySelect || !!onEntityView || (features.customActions?.enabled && (features.customActions.actions?.length ?? 0) > 0);

  // Build visible columns (excluding hidden ones)
  const visibleColumns = useMemo(() => {
    return columns.filter(col => !col.hidden);
  }, [columns]);

  // Build HOT column settings
  const hotColumns = useMemo(() => {
    const cols: Handsontable.ColumnSettings[] = [];

    // Add actions column first if needed
    if (showActionsColumn) {
      cols.push({
        data: '_actions',
        width: 80,
        renderer: createActionsRenderer({
          entityName: config.entityName,
          allowUpdate: features.editing?.allowUpdate || false,
          allowDelete: features.editing?.allowDelete || false,
          // View → onEntityView when provided, else onEntityEdit (long-standing
          // single-handler convention). Edit → onEntityEdit. onEntitySelect is
          // strictly the row-selection callback; it must not bind to the View
          // button or explorers that wire onSelectionChange to a logger (e.g.
          // DLE) lose their View-button action.
          onView: onEntityView ?? onEntityEdit,
          onEdit: features.editing?.allowUpdate ? onEntityEdit : undefined,
          onDelete: features.editing?.allowDelete ? onEntityDelete : undefined,
          customActions: features.customActions?.actions,
          getEntity
        }),
        readOnly: true,
        className: 'htCenter htMiddle actions-column'
      });
    }

    // Add data columns
    visibleColumns.forEach(col => {
      const transformer = config.fieldTransformers?.[col.field as keyof T];

      // Precedence: user-saved width (manually resized + persisted) ›
      // schema width (explicit override) › autoColumnSize (measured).
      const userWidth = savedColumnWidths?.[col.field];
      const effectiveWidth = userWidth ?? col.width;
      cols.push({
        data: col.field,
        ...(effectiveWidth != null ? { width: effectiveWidth } : {}),
        readOnly: true,
        renderer: createTransformerRenderer(transformer, getEntity),
        className: col.sortable ? 'sortable-column' : ''
      });
    });

    return cols;
  }, [visibleColumns, showActionsColumn, config, features, onEntityView, onEntityEdit, onEntityDelete, getEntity, savedColumnWidths]);

  // Build column headers with sort indicators
  const hotHeaders = useMemo(() => {
    const headers: string[] = [];

    // Actions header
    if (showActionsColumn) {
      headers.push('Actions');
    }

    // Data column headers with sort indicators and filter icons
    visibleColumns.forEach(col => {
      const filterKey = col.filterField || col.field;
      headers.push(createSortableHeaderRenderer(
        col.title,
        col.field,
        sortConfig,
        col.sortable,
        col.filterable,
        col.filterable && activeFilters[filterKey] != null,
        col.filterField
      ));
    });

    return headers;
  }, [visibleColumns, showActionsColumn, sortConfig, activeFilters]);

  // Handle header click for sorting or filter icon
  const handleAfterOnCellMouseDown = useCallback((
    event: MouseEvent,
    coords: { row: number; col: number },
    TD: HTMLTableCellElement
  ) => {
    // Only handle header row clicks (row === -1)
    if (coords.row !== -1) return;

    // Adjust column index for actions column
    const colIndex = showActionsColumn ? coords.col - 1 : coords.col;

    // Ignore clicks on actions column
    if (showActionsColumn && coords.col === 0) return;

    // Get the column config
    const column = visibleColumns[colIndex];
    if (!column) return;

    // Filter-icon clicks are handled by the native DOM listener below
    // (added because Handsontable's afterOnCellMouseDown doesn't reliably
    // fire for elements inside column headers). Don't ALSO handle them
    // here — that fires onFilterClick twice and causes a toggle flicker.
    const target = event.target as HTMLElement;
    if (target.closest?.('.column-filter-icon')) {
      event.stopPropagation();
      return;
    }

    // Otherwise handle sort
    if (column.sortable) {
      onSort(column.field);
    }
  }, [visibleColumns, showActionsColumn, onSort]);

  // Handle row click for entity selection
  const handleAfterSelectionEnd = useCallback((
    row: number,
    column: number,
    row2: number,
    column2: number
  ) => {
    // Skip clicks landing on the actions column — the action buttons
    // (View / Edit / Delete) own their own click handlers, and firing
    // onEntitySelect here races with onEntityEdit and clobbers the
    // edit-mode state right after the Edit button sets it.
    if (showActionsColumn && (column === 0 || column2 === 0)) return;

    // Only trigger if selecting a single row and onEntitySelect exists
    if (row === row2 && onEntitySelect) {
      const entity = getEntity(row);
      if (entity) {
        onEntitySelect(entity);
      }
    }
  }, [onEntitySelect, getEntity, showActionsColumn]);

  // HOT settings
  const hotSettings: HotTableProps = useMemo(() => ({
    licenseKey,
    themeName: 'ht-theme-main',
    data: data,
    columns: hotColumns,
    colHeaders: hotHeaders,
    rowHeaders: false,
    readOnly: true,

    // Disable HOT's built-in features (we handle externally)
    columnSorting: false,
    filters: false,
    dropdownMenu: false,

    // Single-cell selection — a click selects one cell, no drag-range.
    // Avoids the jumpy behavior where tiny mouse movement during a click
    // turns into a multi-cell range selection.
    selectionMode: 'single',

    // Enable useful HOT features
    manualColumnResize: true,
    // Explicit header row height — without this HOT applies its own min,
    // which combined with padding pushed rows to ~42px.
    colHeaderHeight: 20,
    // Auto-size columns to fit header + sampled rows. Per-column schema
    // `width` (passed above when set) still wins — autoColumnSize only
    // measures columns where `width` is undefined. samplingRatio limits
    // sampling to ~25% of rendered rows for cheap remeasurement on data
    // refresh. useHeaders:true includes header HTML in the measurement.
    autoColumnSize: { samplingRatio: 0.25, useHeaders: true },

    // Schema min/maxWidth aren't natively supported by HOT per-column —
    // clamp the auto-sized result here. A global default max of 300px
    // stops long-text columns from sprawling; schema maxWidth (when set)
    // overrides the default per-column. Schema minWidth (when set) bumps
    // a too-narrow column up to a floor.
    modifyColWidth: (width: number, col: number) => {
        const actionsOffset = showActionsColumn ? 1 : 0;
        const dataIdx = col - actionsOffset;
        if (dataIdx < 0) return width;
        const meta = visibleColumns[dataIdx];
        if (!meta) return width;
        const cap = meta.maxWidth ?? 300;
        if (width > cap) return cap;
        if (meta.minWidth != null && width < meta.minWidth) return meta.minWidth;
        return width;
    },
    // 'last' keeps columns at their natural width and stretches only the last
    // to fill horizontal space. On narrow containers columns overflow and
    // HOT's internal horizontal scrollbar takes over — which is what we want
    // on mobile/windowed modes where stretchH:'all' squashes columns unusably.
    stretchH: 'last',
    autoWrapRow: true,
    autoWrapCol: true,

    // Performance settings
    renderAllRows: false,
    viewportRowRenderingOffset: 10,

    // Appearance
    className: `htCustomTheme ${config.presentation?.styling?.striped ? 'htStriped' : ''} ${config.presentation?.styling?.hoverable ? 'htHoverable' : ''}`,

    // Height
    height: height,
    autoRowSize: true,

    // Context menu (copy only)
    contextMenu: ['copy'],

    // Event handlers attached via addHook after mount (see below)
    // to avoid recreating hotSettings when callbacks change


    // Row-level styling via config.getRowClassName
    cells: (row: number, col: number) => {
      if (!config.getRowClassName) return {};
      const entity = data[row];
      if (!entity) return {};
      const rowClassName = config.getRowClassName(entity);
      return rowClassName ? { className: rowClassName } : {};
    },

    // Prevent default cell editing
    beforeKeyDown: (event: KeyboardEvent) => {
      // Allow copy (Ctrl+C / Cmd+C)
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        return;
      }
      // Block other editing keys
      if (event.key === 'Enter' || event.key === 'F2') {
        event.stopImmediatePropagation();
      }
    }
  }), [
    licenseKey,
    // Only structural deps — NOT data, height, or callbacks.
    // Callbacks use stable refs below so hotSettings never recreates
    // due to parent re-renders (resize, z-index, etc.)
    hotColumns,
    hotHeaders,
    config.presentation?.styling,
    config.getRowClassName,
  ]);

  // Stable callback refs — hotSettings references these so it never
  // recreates when the parent re-renders with new callback instances
  const cellMouseDownRef = useRef(handleAfterOnCellMouseDown);
  cellMouseDownRef.current = handleAfterOnCellMouseDown;
  const selectionEndRef = useRef(handleAfterSelectionEnd);
  selectionEndRef.current = handleAfterSelectionEnd;

  // Map a HOT column index to the underlying schema field, accounting for
  // the prepended actions column. Fires onColumnResize so the parent can
  // persist the new width.
  const handleAfterColumnResize = useCallback((newSize: number, columnIdx: number) => {
    if (!onColumnResize) return;
    const dataIdx = showActionsColumn ? columnIdx - 1 : columnIdx;
    if (dataIdx < 0) return;
    const colMeta = visibleColumns[dataIdx];
    if (!colMeta) return;
    onColumnResize(colMeta.field, newSize);
  }, [onColumnResize, showActionsColumn, visibleColumns]);
  const columnResizeRef = useRef(handleAfterColumnResize);
  columnResizeRef.current = handleAfterColumnResize;

  // Attach stable wrappers once after mount
  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    // Cancel HOT's mouse-driven cell selection unconditionally. The grid is
    // read-only; selection only ever produced unwanted side effects — the
    // selected cell highlight, the auto-scroll to bring it into view (the
    // "explorer jumps" symptom), and races between selection and the action
    // button click handlers. Keyboard navigation (which doesn't go through
    // this hook) still works for accessibility.
    hot.addHook('beforeOnCellMouseDown', (event: MouseEvent, _coords: any, _td: HTMLTableCellElement, controller: { row: boolean; column: boolean; cell: boolean }) => {
      // Scott's hardening: suppress HOT's auto-selection on mouse-down to
      // stop the "explorer jumps" / "action button race" symptoms that
      // come from left-click landing on a row mid-render.
      //
      // Important to scope to LEFT button only — the right-click path
      // (event.button === 2) needs to select the cell so HOT's
      // contextMenu: ['copy'] plugin has a target to copy. Without this
      // narrowing the right-click menu opens but Copy is a no-op (#524).
      if (event.button === 0) {
        controller.row = true;
        controller.column = true;
        controller.cell = true;
      }
    });
    hot.addHook('afterOnCellMouseDown', (event: MouseEvent, coords: any, td: HTMLTableCellElement) => cellMouseDownRef.current(event, coords, td));
    hot.addHook('afterSelectionEnd', (row: number, col: number, row2: number, col2: number) => selectionEndRef.current(row, col, row2, col2));
    // Bubble manual column resizes up so the parent can persist them per
    // user. Use a ref so the latest callback closure is invoked even though
    // the hook itself is registered once on mount.
    hot.addHook('afterColumnResize', (newSize: number, columnIdx: number) => columnResizeRef.current(newSize, columnIdx));
  }, []);

  // Track the user's last horizontal scroll position via a capture-phase
  // listener. Capture phase because scroll events don't bubble, and we filter
  // on `isTrusted` so HOT's own programmatic scroll resets (loadData /
  // updateSettings both reset to 0) don't poison the ref. This lets us restore
  // the user's column position even when the reset happens BEFORE our data
  // useEffect runs (e.g. updateSettings fires on sort click before fetch).
  // Track the user's last horizontal scroll position via a capture-phase
  // listener. Capture phase because scroll events don't bubble; isTrusted
  // filter skips programmatic resets (HOT's loadData/updateSettings) so the
  // ref always holds the user's actual position.
  const userScrollLeftRef = useRef(0);
  useEffect(() => {
    const root = hotRef.current?.hotInstance?.rootElement as HTMLElement | undefined;
    if (!root) return;
    const onScroll = (e: Event) => {
      if (!e.isTrusted) return;
      const t = e.target as HTMLElement | null;
      if (t?.classList?.contains?.('wtHolder')) {
        userScrollLeftRef.current = t.scrollLeft;
      }
    };
    root.addEventListener('scroll', onScroll, true);
    return () => root.removeEventListener('scroll', onScroll, true);
  }, []);

  // Reload table data only when the actual content changes (not just the reference).
  const prevDataRef = useRef<typeof data | null>(null);
  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    // Skip if data reference is the same OR if content is identical
    if (prevDataRef.current === data) return;
    if (
      prevDataRef.current &&
      prevDataRef.current.length === data.length &&
      JSON.stringify(prevDataRef.current) === JSON.stringify(data)
    ) {
      prevDataRef.current = data;
      return;
    }

    prevDataRef.current = data;
    hot.loadData(data);

    // Restore horizontal scroll position. loadData resets the table to (0,0);
    // we want the user's column position to survive sort/filter/page changes.
    // Vertical scroll resets — that's the right default when the row set changes.
    const target = userScrollLeftRef.current;
    if (target > 0) {
      requestAnimationFrame(() => {
        const h = hot.rootElement?.querySelector('.ht_master .wtHolder') as HTMLElement | null;
        if (h) h.scrollLeft = target;
      });
    }
  }, [data]);

  // Settings changes (e.g. sort click rebuilds colHeaders → updateSettings)
  // also reset scroll, BEFORE the data fetch returns. Restore here too so the
  // user doesn't see an interim flash to the leftmost column.
  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const target = userScrollLeftRef.current;
    if (target <= 0) return;
    requestAnimationFrame(() => {
      const h = hot.rootElement?.querySelector('.ht_master .wtHolder') as HTMLElement | null;
      if (h && h.scrollLeft !== target) h.scrollLeft = target;
    });
  }, [hotSettings]);

  // Native DOM click handler for filter icons (Handsontable's afterOnCellMouseDown
  // doesn't reliably fire for clicks on elements inside column headers)
  useEffect(() => {
    const container = hotRef.current?.hotInstance?.rootElement;
    if (!container || !onFilterClick) return;

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const filterIcon = target.closest?.('.column-filter-icon');
      if (filterIcon) {
        const field = filterIcon.getAttribute('data-filter-field');
        if (field) {
          e.stopPropagation();
          onFilterClick(field);
        }
      }
    };

    container.addEventListener('click', handleClick, true);
    return () => container.removeEventListener('click', handleClick, true);
  }, [onFilterClick]);

  // The HotTable must always mount (even with empty data) so the cell-
  // selection-cancel hook in the useEffect above attaches to a real
  // hotInstance. Previously this early-returned when data was empty, but
  // ResultsSection now keeps HOTDataGrid mounted across loading transitions —
  // an early-return on initial empty data would prevent the hooks from ever
  // attaching, so HOT's default cell selection would take over after data
  // arrives. Show "No X found" as an overlay instead.
  return (
    <div className="hot-data-grid-container" style={{ position: 'relative' }}>
      <MemoizedHotTable
        hotRef={hotRef}
        settings={hotSettings}
      />
      {data.length === 0 && (
        <div
          className="hot-data-grid-empty"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <p>No {config.entityName.toLowerCase()}s found</p>
        </div>
      )}
    </div>
  );
}

// Prevent HotTable from re-rendering (and calling updateSettings) on parent re-renders.
// Only re-renders when the memoized hotSettings object identity changes.
const MemoizedHotTable = React.memo(
  ({ hotRef, settings }: { hotRef: React.RefObject<HotTableRef | null>; settings: HotTableProps }) => (
    <HotTable ref={hotRef} {...settings} />
  ),
  (prev, next) => prev.settings === next.settings,
);
